import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { FlatList, ScrollView, SectionList, TouchableOpacity, View } from 'react-native'

import api from "../../../../../utils/api"
import tw from '../../../../../styles/tailwind';
import Text from '../../../../../components/Text';
import Icon from '../../../../../components/IcomoonIcon';
import { Image } from 'expo-image';
import { useQuery } from '@tanstack/react-query';
import { useNavigation } from '@react-navigation/native';

import dayjs from 'dayjs'
import IconButton from '../../../../../components/IconButton';
import { currency, money } from '../Reports/ReportsSection';
import { MonthSelector, MonthSelectorDropdown, MonthSelectorModal } from '../../../../../components/Forms/MonthSelector';
import PaymentDetailModal from '../Fees/Payments/PaymentDetailModal';
import SearchPaymentModal from './SearchPaymentModal';


const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"]


function PendingPaymentEvidencesNotificationButton({ style = {}, onPress, count }) {
  return (
    <TouchableOpacity style={tw.style(`row items-center bg-amber-50 border border-amber-200 rounded-xl px-4 py-3`, style)} onPress={onPress}>
      <Text style={tw`text-amber-700 font-500 flex-1 pr-3`}>Tienes {count} comprobantes de pago pendientes de aprobación</Text>

      <Icon name='chevron-right' size={15} color={tw.color('amber-700')} />
    </TouchableOpacity>
  )
}

function PaymentRow({ payment, onPress }) {
  const dateStr = dayjs(payment.apply_date).format('D-MMM-YYYY')

  return (
    <TouchableOpacity onPress={onPress} style={tw`bg-white rounded-2xl border border-gray-200 px-4 py-3 mb-3`}>
      <View style={tw`row justify-between items-start`}>
        <Text style={tw`flex-1 text-secondary text-base`}>{payment.folio}</Text>

        <Text style={tw`text-green-500 font-500`}>${payment.amount.toLocaleString()}</Text>
      </View>

      <View style={tw`row justify-between items-start`}>
        <View style={tw``}>
          <Text style={tw`text-gray-500`}>{dateStr}</Text>

          {
            payment.payment_method &&
            <Text style={tw`text-gray-500`}>{payment.payment_method.name}</Text>
          }

          {
            payment.unit &&
            <Text style={tw`text-gray-500`}>{payment.unit.name}</Text>
          }

          {
            payment.fee_units.map(fee_unit => 
              <View style={tw``}>
                <Text style={tw`text-gray-500`}>{fee_unit.fee?.name}</Text>
              </View>
            )
          }
        </View>

        <View style={tw`items-end`}>
          <Text style={tw`text-gray-500`}>{payment.category?.name}</Text>
        </View>
      </View>
    </TouchableOpacity>
  )
}


const PaymentsSection = forwardRef(function (props, ref) {
  const { communityId } = props
  const navigation = useNavigation()
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [showMonthSelectorModal, setShowMonthSelectorModal] = useState(false);
  const [shownPaymentId, setShownPaymentId] = useState();
  const [showPaymentSearchModal, setShowPaymentSearchModal] = useState(false);

  const monthISO = selectedMonth.toISOString().split("T")[0]

  const { isInitialLoading, error, data: payments, refetch } = useQuery({
    queryKey: ['community.accounting.payments', communityId, monthISO],
    queryFn: () => api.get(`/communities/${communityId}/payments?month=${monthISO}`).then(response => response.data),
  })

  // Comprobantes de pago pendientes de validar (de cualquier tipo de cuota).
  const { data: feesOverview } = useQuery({
    queryKey: ['community.fees.overview', communityId],
    queryFn: () => api.get(`/communities/${communityId}/fees/overview`).then(response => response.data),
  })

  const periodLabel = `${MONTHS[selectedMonth.getMonth()]} ${selectedMonth.getFullYear()}`

  const periodTotal = useMemo(() => {
    return payments?.reduce((acc, cur) => acc + cur.amount, 0)
  }, [payments])

  // Desglose por concepto, calculado desde los pagos del mes (consistente con el total).
  // Un pago es "mantenimiento" si incluye una cuota principal; el resto, cuotas adicionales.
  const breakdown = useMemo(() => {
    let mantenimiento = 0, adicionales = 0
    ;(payments ?? []).forEach(p => {
      const units = p.fee_units ?? []
      const isMain = units.some(fu => fu.fee?.type?.type === 'main' || /manten/i.test(fu.fee?.name ?? ''))
      if (isMain) mantenimiento += p.amount
      else adicionales += p.amount
    })
    return { mantenimiento, adicionales }
  }, [payments])


  useImperativeHandle(ref, function getRefValue() {
    return {
      setShowPaymentSearchModal(value) { setShowPaymentSearchModal(value) },
    }
  }, [])



  const listHeader = (
    <View>
      {/* Card de ingresos del mes */}
      <View style={tw`bg-[#EAFBF9] border border-primary rounded-2xl px-5 py-4 mb-4`}>
        <TouchableOpacity
          style={tw`row justify-between items-center bg-primary/15 rounded-xl px-4 py-3 mb-4`}
          onPress={() => setShowMonthSelectorModal(true)}
        >
          <Text style={tw`text-secondary font-500`}>{periodLabel}</Text>
          <Icon name='chevron-right' size={13} color={tw.color('secondary')} style={{ transform: [{ rotateZ: '90deg' }] }} />
        </TouchableOpacity>

        <Text style={tw`text-gray-500`}>Total de ingresos</Text>
        <Text style={tw`text-primary font-700 text-4xl`}>{money(periodTotal)}</Text>
        <Text style={tw`text-gray-400 text-sm mt-1`}>
          {(payments?.length ?? 0)} pago{(payments?.length ?? 0) !== 1 ? 's' : ''} registrado{(payments?.length ?? 0) !== 1 ? 's' : ''}
        </Text>

        <View style={tw`border-t border-primary/30 mt-4 pt-3 gap-y-2`}>
          <View style={tw`row justify-between`}>
            <Text style={tw`text-gray-600`}>Mantenimiento</Text>
            <Text style={tw`text-gray-800 font-500`}>{money(breakdown.mantenimiento)}</Text>
          </View>
          <View style={tw`row justify-between`}>
            <Text style={tw`text-gray-600`}>Cuotas adicionales</Text>
            <Text style={tw`text-gray-800 font-500`}>{money(breakdown.adicionales)}</Text>
          </View>
        </View>
      </View>

      {
        feesOverview?.pending_evidences_count > 0 &&
          <PendingPaymentEvidencesNotificationButton
            style={`mb-4`}
            onPress={() => navigation.navigate("Admin.Payments.Evidences", { communityId })}
            count={feesOverview?.pending_evidences_count}
          />
      }

      {
        isInitialLoading &&
          <Text style={tw`my-8 text-lg text-center text-gray-400`}>Cargando...</Text>
      }

      {
        !isInitialLoading && payments?.length == 0 &&
          <Text style={tw`mt-10 text-lg text-center text-gray-400`}>Sin pagos registrados</Text>
      }
    </View>
  )

  return (
    <View style={tw`flex-1 bg-[#F3F2F3]`}>
      <FlatList
        data={payments}
        ListHeaderComponent={listHeader}
        contentContainerStyle={tw`px-4 pt-4 pb-14`}
        renderItem={({ item }) => <PaymentRow onPress={() => setShownPaymentId(item.uuid)} payment={item} />}
        keyExtractor={(item) => item.uuid}
      />

      {
        showMonthSelectorModal &&
          <MonthSelectorModal
            onRequestClose={() => setShowMonthSelectorModal(false)}
            selectedDate={selectedMonth}
            onChange={date => {
              setShowMonthSelectorModal(false)
              setSelectedMonth(date)
            }}
          />
      }

      {
        shownPaymentId &&
          <PaymentDetailModal
            onRequestClose={() => setShownPaymentId()}
            paymentId={shownPaymentId}
            onPaymentEdited={refetch}
            onPaymentDeleted={() => {
              setShownPaymentId()
              refetch()
            }}
          />
      }

      {
        showPaymentSearchModal &&
          <SearchPaymentModal
            onRequestClose={() => setShowPaymentSearchModal(false)}
            onPaymentFound={payment => {
              setShowPaymentSearchModal(false)
              setShownPaymentId(payment.uuid)
            }}
          />
      }

    </View>
  )
})

export default PaymentsSection